import { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { signOut } from "../../lib/sloeAuthClient";
import { OSSchema, ScreenTemplate, resolveScreenNoun } from "@sloe/schema";
import { ai } from "../../lib/gemini";
import { trackEvent } from "../../lib/telemetry";
import { DEFAULT_BRAND_COLOR, interactiveAccent } from "../../lib/brand";
import { generateSeedData } from "../../lib/seed-data-generator";
import { Sparkles, Send, X, Settings, GitBranch, Plug, ChevronRight, ChevronDown, Check, ClipboardList, Menu } from "lucide-react";
import { EvolutionCenter } from "./EvolutionCenter";
import { IntegrationHub } from "./IntegrationHub";
import { WhatThisOSIsSolving } from "./WhatThisOSIsSolving";
import { DashboardContextStrip } from "./DashboardContextStrip";
import { EstimatedLostRevenue } from "./EstimatedLostRevenue";
import { FirstStepsCard } from "./FirstStepsCard";
import { WelcomeScreen } from "./WelcomeScreen";
import SettingsScreen from "../SettingsScreen";
import { Spotlight } from "../evolution/Spotlight";
import { UpdateToast } from "../UpdateToast";
import { AgenticOSWalkthrough } from "../AgenticOSWalkthrough";
import { ErrorBoundary } from "../ErrorBoundary";
import { MissionControl } from "../MissionControl";
import AuditScreen from "./AuditScreen";
import { ComponentRenderer, KanbanRenderer } from "./ComponentRenderer";
import { PaymentWall } from "./PaymentWall";
import { DetailDrawer } from "./DetailDrawer";
import { useOsStore } from "../../lib/os-store";
import { notifySafeCheckpoint } from "../../lib/update-reload";

// Code-split: the Operator surface (threads UI + Operator + react-markdown +
// the voice-mode tree) is deferred out of the boot chunk and loaded on demand
// when the user opens the Operator screen. Named-export → default wrapper.
const OperatorWithThreads = lazy(() =>
  import("./OperatorWithThreads").then((m) => ({ default: m.OperatorWithThreads })),
);

interface OwnedOS {
  id: string;
  subdomain_slug: string;
  os_name: string;
  brand_color: string;
}

interface SchemaRendererProps {
  schema: OSSchema;
  currentScreenId?: string;
  workspaceId?: string;
  userFirstName?: string;
  embedded?: boolean;
  onAskOperator?: (prompt: string) => void;
  // Forwarded to the standalone Settings Profile section (the real session user).
  user?: { id: string; email: string } | null;
}

// Shape returned by /api/screen-data. `data` is the row's user-facing
// payload; we flatten it with the DB id on top before handing to renderers
// so existing code that reads `record.name` / `record.stage` etc. keeps
// working. The DB id wins over any `id` inside `data`.
interface ScreenDataRow {
  id: string;
  position: number;
  data: Record<string, any>;
}

type WorkspaceView = {
  screen_id: string;
  label: string;
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; rows: ScreenDataRow[] };

function screenActionPrompts(screen: ScreenTemplate, recordCount?: number): Array<{ label: string; prompt: string }> {
  const componentTypes = new Set((screen.components || []).map((c) => c.type));
  const screenLabel = screenDisplayLabel(screen);
  const screenRef = `${screenLabel} screen_id:${screen.screen_id}`;
  const sourcePrompt =
    `This ${screenRef} needs live data. Help me fill it from my existing source. ` +
    `Ask me where this data lives right now. If I name a connected file/tracker source, use the file search tool to find it; ` +
    `if it is a connected sheet, use the sheet preview tool for screen_id "${screen.screen_id}"; if it is a CSV, use the CSV import tool only after I clearly confirm that I want those rows imported. ` +
    `Only call import tools after I clearly confirm that I want those rows brought into the Agentic Operating System.`;

  if (screen.screen_id === "dashboard" || screen.layout === "dashboard") {
    return [
      { label: "Triage the Agentic Operating System", prompt: "Triage this whole Agentic Operating System. Call triage_collection without a screen_id and tell me the top priorities across the workspace with the exact record facts behind each one." },
      { label: "Project this week", prompt: "Project this week across the Agentic Operating System. Call project_outlook without a screen_id and give me the likely direction, drivers, risks, and confidence." },
      { label: "Find missing data", prompt: "Look across the Agentic Operating System and tell me what live data source we should connect first. If no tools are connected, ask me one tight question about where the data lives." },
    ];
  }

  if (recordCount === 0 && screen.layout !== "settings") {
    return [
      { label: "Fill from data", prompt: sourcePrompt },
      { label: "Find my tracker", prompt: `Help me find the source for the ${screenRef}. Ask for the tracker/file name, then use the connected file search tool. Preview the best sheet match before importing anything; if the match is a CSV, wait for my confirmation before calling the CSV import tool.` },
      { label: "Add first record", prompt: `Help me add the first real record to the ${screenRef}. Ask for only the missing fields you need, then use create_record for screen_id "${screen.screen_id}".` },
    ];
  }

  if (screen.layout === "kanban" || componentTypes.has("kanban")) {
    return [
      { label: "Triage this pipeline", prompt: `Triage the ${screenRef}. Call triage_collection with screen_id "${screen.screen_id}" and tell me the top priorities with the exact record facts behind each one.` },
      { label: "Find blocked items", prompt: `Look at the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and find anything blocked, stale, or sitting in the wrong stage.` },
      { label: "Project next moves", prompt: `Project the ${screenRef}. Use project_outlook with screen_id "${screen.screen_id}" and tell me what is likely to happen next plus the risks.` },
    ];
  }
  if (screen.layout === "schedule_grid" || componentTypes.has("schedule_block_grid")) {
    return [
      { label: "Review this week", prompt: `Review this week's schedule on the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and flag overdue, unscheduled, or conflicting items.` },
      { label: "Find gaps", prompt: `Find schedule gaps in the ${screenRef}. Use screen_id "${screen.screen_id}" and tell me what needs a date, owner, or follow-up.` },
      { label: "Connect calendar", prompt: `This ${screenLabel} screen depends on calendar/source data. Ask me where this data lives right now, then offer the right connector if available.` },
    ];
  }
  if (screen.layout === "portfolio_grid" || componentTypes.has("portfolio_card_grid")) {
    return [
      { label: "Find risk", prompt: `Review the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and group the portfolio by status, risk, or asset class. Tell me what needs attention first.` },
      { label: "Compare value", prompt: `Compare the items on the ${screenRef}. Use screen_id "${screen.screen_id}" and highlight value/risk concentration or missing valuation fields.` },
    ];
  }
  if (screen.layout === "gallery_grid" || componentTypes.has("gallery_card_grid")) {
    return [
      { label: "Review approvals", prompt: `Review the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and find creative work waiting on approval, client feedback, or next action.` },
      { label: "Sort by channel", prompt: `Sort the ${screenRef} by client, channel, or status. Use screen_id "${screen.screen_id}" and tell me which work should move next.` },
    ];
  }
  if (screen.layout === "production_board" || componentTypes.has("production_run_grid")) {
    return [
      { label: "Check throughput", prompt: `Check throughput on the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and flag anything under target or blocked by station/status.` },
      { label: "Unblock work", prompt: `Triage the ${screenRef}. Use triage_collection with screen_id "${screen.screen_id}" and give me the next unblock move.` },
    ];
  }
  if (componentTypes.has("scored_buckets")) {
    return [
      { label: "Rank decisions", prompt: `Rank the qualification decisions on the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and explain HOT/WARM/COLD/SKIP using only row data.` },
      { label: "Explain verdicts", prompt: `Review verdict, fit, intent, positives, and concerns on the ${screenRef}. Use screen_id "${screen.screen_id}" and tell me which records deserve action.` },
    ];
  }
  if (componentTypes.has("development_timeline")) {
    return [
      { label: "Review progress", prompt: `Review development progress on the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and separate verified facts from missing history.` },
      { label: "Find next focus", prompt: `Find the next development focus on the ${screenRef}. Use screen_id "${screen.screen_id}" and tell me who or what needs attention.` },
    ];
  }
  return [
    { label: "What needs attention?", prompt: `Look at the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and tell me what needs attention first.` },
    { label: "Summarize records", prompt: `Summarize the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and show the useful patterns in the records.` },
    { label: "Find missing fields", prompt: `Review the ${screenRef}. Use get_screen_rows for screen_id "${screen.screen_id}" and tell me whether this screen is missing any field that would make the work easier.` },
  ];
}

function screenEyebrow(screen: ScreenTemplate): string {
  if (screen.screen_id === "dashboard" || screen.layout === "dashboard") return "Overview";
  if (screen.layout === "kanban") return "Pipeline";
  if (screen.layout === "list") return "Workspace";
  if (
    screen.layout === "portfolio_grid" ||
    screen.layout === "gallery_grid" ||
    screen.layout === "schedule_grid" ||
    screen.layout === "production_board"
  ) return "Operations";
  if (screen.layout === "settings") return "System";
  return "Workspace";
}

function screenDisplayLabel(screen?: Pick<ScreenTemplate, "label" | "screen_id"> | null): string {
  return String(screen?.label ?? screen?.screen_id ?? "screen").trim() || "screen";
}

function screenSubtitle(screen: ScreenTemplate, recordCount?: number): string | null {
  if (typeof recordCount !== "number") return null;
  if (screen.layout === "settings") return null;
  const noun = resolveScreenNoun(screen, "record");
  if (recordCount === 0) return `Ready for your first ${noun.singular}.`;
  if (recordCount === 1) return `1 live ${noun.singular} in this screen.`;
  return `${recordCount} live ${noun.plural} in this screen.`;
}

function screenOverviewCopy(screen: ScreenTemplate): { eyebrow: string; title: string; body: string } {
  if (screen.screen_id === "dashboard" || screen.layout === "dashboard") {
    return {
      eyebrow: "Overview",
      title: "Start with the big picture, then narrow in.",
      body: "This is the command center for the workspace. Use it to see what is live, what is blocked, and which surface needs attention next.",
    };
  }
  if (screen.layout === "kanban") {
    return {
      eyebrow: "Pipeline",
      title: "Move work through stages instead of scanning a flat list.",
      body: "This view is built for flow. It highlights stalled items, stage imbalance, and the records that should move next.",
    };
  }
  if (screen.layout === "list") {
    return {
      eyebrow: "Workspace",
      title: "Review records quickly, then open the one that matters.",
      body: "This view is for fast browsing and focused edits. It works best when the user can sort, scan, and drill into a single row without friction.",
    };
  }
  if (screen.layout === "schedule_grid") {
    return {
      eyebrow: "Operations",
      title: "See what is happening now and what still needs a date.",
      body: "This schedule view groups records by day so the user can spot conflicts, gaps, and unscheduled work at a glance.",
    };
  }
  if (screen.layout === "portfolio_grid") {
    return {
      eyebrow: "Operations",
      title: "Compare the whole portfolio before opening a card.",
      body: "This screen is for scanning status, value, and ownership across a set of items that need ranked attention.",
    };
  }
  if (screen.layout === "gallery_grid") {
    return {
      eyebrow: "Operations",
      title: "Use the visual grid to review creative work quickly.",
      body: "This view is best when appearance, status, and approval state matter more than a dense table.",
    };
  }
  if (screen.layout === "production_board") {
    return {
      eyebrow: "Operations",
      title: "Track throughput and find the bottleneck fast.",
      body: "This board is designed to make progress, capacity, and stalls obvious before the user opens a record.",
    };
  }
  if (screen.layout === "settings") {
    return {
      eyebrow: "System",
      title: "Account, access, and support live here.",
      body: "This screen should answer how to change the owner account, repair access, and request help without exposing the rest of the workspace.",
    };
  }
  return {
    eyebrow: "Workspace",
    title: "Use this screen to understand what matters and what to do next.",
    body: "Every generated screen should make the current work obvious before the user scrolls into the data blocks.",
  };
}

function WorkspaceViewPicker({
  views,
  activeScreenId,
  onSelectView,
}: {
  views: WorkspaceView[];
  activeScreenId: string;
  onSelectView: (screenId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const currentView = views.find((view) => view.screen_id === activeScreenId) || views[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!currentView || views.length < 2) return null;

  return (
    <div ref={pickerRef} className="relative w-full md:w-72">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50"
      >
        <span className="min-w-0">
          <span className="block text-[10px] font-medium uppercase tracking-[0.14em] text-zinc-400">
            Workspace view
          </span>
          <span className="block truncate text-[13px] font-medium text-zinc-800">
            {currentView.label}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-full overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl">
          {views.map((view) => {
            const active = view.screen_id === activeScreenId;
            return (
              <button
                key={view.screen_id}
                type="button"
                onClick={() => {
                  onSelectView(view.screen_id);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[13px] transition-colors ${
                  active ? "bg-zinc-50 text-zinc-900" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{view.label}</span>
                {active && <Check size={14} className="shrink-0 text-zinc-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScreenHeader({
  screen,
  recordCount,
  workspaceViews,
  activeScreenId,
  onSelectWorkspaceView,
}: {
  screen: ScreenTemplate;
  recordCount?: number;
  workspaceViews?: WorkspaceView[];
  activeScreenId?: string;
  onSelectWorkspaceView?: (screenId: string) => void;
}) {
  const eyebrow = screenEyebrow(screen);
  const subtitle = screenSubtitle(screen, recordCount);
  const label = screenDisplayLabel(screen);
  return (
    <header className="mb-7 md:mb-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-400">
            {eyebrow}
          </p>
          <h1 className="mt-2 text-[26px] md:text-[28px] font-semibold tracking-tight text-zinc-950">
            {label}
          </h1>
          {subtitle && (
            <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-zinc-500">
              {subtitle}
            </p>
          )}
        </div>
        {screen.layout !== "settings" && workspaceViews && onSelectWorkspaceView && activeScreenId && workspaceViews.length > 1 && (
          <WorkspaceViewPicker
            views={workspaceViews}
            activeScreenId={activeScreenId}
            onSelectView={onSelectWorkspaceView}
          />
        )}
      </div>
    </header>
  );
}

function ScreenActionStrip({
  screen,
  recordCount,
  onAskOperator,
  brandColor,
}: {
  screen: ScreenTemplate;
  recordCount?: number;
  onAskOperator?: (prompt: string) => void;
  brandColor?: string;
}) {
  if (!onAskOperator) return null;
  const prompts = screenActionPrompts(screen, recordCount).slice(0, 3);
  return (
    <div className="mb-7 rounded-2xl border border-zinc-200/80 bg-white/90 px-4 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:px-5">
      <div className="flex flex-col gap-3 md:gap-3.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-400">
            Operator Moves
          </p>
          <p className="mt-1 text-[13px] text-zinc-500">
            Fast ways to work this screen with the Operator.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {prompts.map((p, index) => (
            <button
              key={p.label}
              type="button"
              onClick={() => onAskOperator(p.prompt)}
              style={index === 0 && brandColor ? { background: `${brandColor}14`, borderColor: `${brandColor}30`, color: brandColor } : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[12.5px] font-medium transition-colors ${
                index === 0
                  ? "text-zinc-900"
                  : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-950"
              }`}
            >
              <Sparkles size={13} />
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScreenOverviewPanel({
  screen,
  recordCount,
  onAskOperator,
}: {
  screen: ScreenTemplate;
  recordCount: number;
  onAskOperator?: (prompt: string) => void;
}) {
  const copy = screenOverviewCopy(screen);
  const prompts = screenActionPrompts(screen, recordCount).slice(0, 2);
  const noun = resolveScreenNoun(screen, "record");
  return (
    <section className="mb-6 rounded-3xl border border-zinc-200 bg-white px-5 py-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:px-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 max-w-2xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">
            {copy.eyebrow}
          </p>
          <h2 className="mt-2 text-[17px] font-semibold tracking-tight text-zinc-950 md:text-[18px]">
            {copy.title}
          </h2>
          <p className="mt-2 text-[13px] leading-relaxed text-zinc-500">
            {copy.body}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {recordCount} live {recordCount === 1 ? noun.singular : noun.plural}
            </span>
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {screen.layout.replace(/_/g, " ")}
            </span>
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {screen.components.length} component{screen.components.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        {onAskOperator && prompts.length > 0 && (
          <div className="flex flex-col gap-2 lg:w-[24rem]">
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">
              Next move
            </div>
            {prompts.map((prompt) => (
              <button
                key={prompt.label}
                type="button"
                onClick={() => onAskOperator(prompt.prompt)}
                className="flex items-start gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-3.5 py-3 text-left text-[13px] font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-white hover:text-zinc-950"
              >
                <Sparkles size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                <span>{prompt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ScreenContent({ screen, schema, workspaceId, onItemClick, selectedKpiLabel, onNavigateScreen, onAskOperator, mission, painPoints, hasCharter, multiEntity, entityCount, country, currencies, revenue, workspaceViews, activeScreenId, onSelectWorkspaceView }: { screen: ScreenTemplate; schema: OSSchema; workspaceId?: string; onItemClick?: (item: any) => void; selectedKpiLabel?: string | null; onNavigateScreen?: (screenId: string) => void; onAskOperator?: (prompt: string) => void; mission?: string; painPoints?: string[]; hasCharter: boolean | null; multiEntity?: boolean; entityCount?: number; country?: string; currencies?: any[]; revenue?: { value: number; currency: string } | null; workspaceViews?: WorkspaceView[]; activeScreenId?: string; onSelectWorkspaceView?: (screenId: string) => void }) {
  const screenLabel = screenDisplayLabel(screen);
  const seedData = useMemo(
    () => generateSeedData(schema.metadata.industry_shape, screenLabel),
    [schema.metadata.industry_shape, screenLabel]
  );

  // No fallback to schema.id — that's the schema-version identifier
  // (e.g. "schema-kingkay-test-v1"), not the workspace UUID. Anywhere we
  // need a workspace_id for an API call or telemetry we use this prop value directly.
  const effectiveWorkspaceId = workspaceId || "";
  const [fetchState, setFetchState] = useState<FetchState>({ status: "loading" });
  const [refreshKey, setRefreshKey] = useState(0);
  // Editors increment this on successful save → triggers a refetch here.
  const screenRefreshKey = useOsStore((s) => s.screenRefreshKey);
  const setScreenRows = useOsStore((s) => s.setScreenRows);

  // Re-fetch on screen / os change. AbortController prevents a stale
  // response from clobbering state if the user switches screens mid-fetch.
  useEffect(() => {
    setFetchState({ status: "loading" });
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(
          `/api/screen-data/${encodeURIComponent(effectiveWorkspaceId)}/${encodeURIComponent(screen.screen_id)}`,
          { credentials: "include", signal: ctrl.signal },
        );
        const data = await res.json();
        if (ctrl.signal.aborted) return;
        if (!res.ok || !data?.ok) {
          setFetchState({ status: "error", message: data?.error || `HTTP ${res.status}` });
          return;
        }
        setFetchState({ status: "ok", rows: Array.isArray(data.rows) ? data.rows : [] });
      } catch (err: any) {
        if (ctrl.signal.aborted) return;
        setFetchState({ status: "error", message: err?.message || "Network error" });
      }
    })();
    return () => {
      ctrl.abort();
      // Clear cached rows when the screen/os changes so editors don't see
      // stale distinct-value options during a transition.
      setScreenRows(null, null, []);
    };
  }, [effectiveWorkspaceId, screen.screen_id, refreshKey, screenRefreshKey, setScreenRows]);

  // Decide between real data and empty state.
  // - rows > 0  → real records flow to the renderer
  // - rows === 0 → empty state
  const flattened: any[] | null = fetchState.status === "ok"
    ? fetchState.rows.map(r => ({ ...r.data, id: r.id }))
    : null;
  const records = fetchState.status === "ok" ? flattened! : [];
  const [activeFilter, setActiveFilter] = useState<{ field: string; value: string | null } | null>(null);
  const [activeSearch, setActiveSearch] = useState<{ field: string; query: string } | null>(null);

  useEffect(() => {
    setActiveFilter(null);
    setActiveSearch(null);
  }, [screen.screen_id]);

  const visibleRecords = useMemo(() => {
    let next = records;
    if (activeFilter?.value) {
      next = next.filter((record) => String(record?.[activeFilter.field] ?? "") === activeFilter.value);
    }
    const query = String(activeSearch?.query ?? "").trim().toLowerCase() || "";
    if (query) {
      const searchField = activeSearch?.field;
      next = next.filter((record) => {
        if (searchField && record?.[searchField] != null) {
          return String(record[searchField]).toLowerCase().includes(query);
        }
        return Object.values(record || {}).some((value) => String(value ?? "").toLowerCase().includes(query));
      });
    }
    return next;
  }, [records, activeFilter, activeSearch]);
  const controlProps = {
    filterState: activeFilter,
    onFilterChange: (field: string, value: string | null) => setActiveFilter({ field, value }),
    searchState: activeSearch,
    onSearchChange: (field: string, query: string) => setActiveSearch({ field, query }),
  };
  const recordsForComponent = (componentType: string) => (componentType === "filter" ? records : visibleRecords);

  // Publish real (non-seed) rows to the store so editors can read distinct
  // values when ScreenField.options is empty. Seed rows aren't published —
  // they aren't editable and we don't want them polluting the cache.
  useEffect(() => {
    if (fetchState.status === "ok" && flattened && flattened.length > 0) {
      setScreenRows(effectiveWorkspaceId, screen.screen_id, flattened);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchState.status, effectiveWorkspaceId, screen.screen_id]);

  // Loading + error are screen-level concerns regardless of layout.
  if (fetchState.status === "loading") {
    return (
      <div className="p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">{screenLabel}</h1>
          <p className="text-sm text-zinc-500 mt-1">Loading…</p>
        </header>
        <div className="space-y-3 animate-pulse">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-zinc-100" />
          ))}
        </div>
      </div>
    );
  }

  if (fetchState.status === "error") {
    return (
      <div className="p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">{screenLabel}</h1>
        </header>
        <div className="rounded-xl border border-rose-100 bg-rose-50 p-6 max-w-lg">
          <p className="text-sm font-medium text-rose-900 mb-1">Couldn't load this screen.</p>
          <p className="text-xs text-rose-700 mb-4">{fetchState.message}</p>
          <button
            type="button"
            onClick={() => setRefreshKey(k => k + 1)}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-rose-900 text-white hover:bg-rose-800"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const empty = records.length === 0;

  // ─── Kanban layout ───
  if (screen.layout === "kanban") {
    const aboveTypes = new Set(["filter", "search", "stat_row"]);
    const aboveComponents = screen.components.filter((c) => aboveTypes.has(c.type));
    const boardComponents = screen.components.filter((c) => c.type === "kanban");
    return (
      <div className="px-6 py-7 md:px-8 md:py-8">
        <ScreenHeader
          screen={screen}
          recordCount={records.length}
          workspaceViews={workspaceViews}
          activeScreenId={activeScreenId}
          onSelectWorkspaceView={onSelectWorkspaceView}
        />
        <ScreenActionStrip
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
          brandColor={schema.metadata?.brand_color}
        />
        <ScreenOverviewPanel
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
        />
        <div className="flex flex-col gap-6">
          {aboveComponents.length > 0 && (
            <div className="flex flex-col gap-4">
              {aboveComponents.map((c) => (
                <ComponentRenderer
                  key={c.component_id}
                  component={c}
                  schema={schema}
                  screen={screen}
                  workspaceId={effectiveWorkspaceId}
                  records={recordsForComponent(c.type)}
                  onNavigateScreen={onNavigateScreen}
                  {...controlProps}
                />
              ))}
            </div>
          )}
          {boardComponents.length > 0 ? (
            boardComponents.map((c) => (
              <ComponentRenderer
                key={c.component_id}
                component={c}
                schema={schema}
                screen={screen}
                workspaceId={effectiveWorkspaceId}
                records={visibleRecords}
                onNavigateScreen={onNavigateScreen}
                {...controlProps}
              />
            ))
          ) : (
            <KanbanRenderer seedData={seedData} records={visibleRecords} schema={schema} workspaceId={effectiveWorkspaceId} screen={screen} />
          )}
        </div>
      </div>
    );
  }

  // ─── List layout — Phase 5-A ───
  // Splits the screen's components into a thin inline row of filter/search
  // helpers above the main column (list, table, etc.). Empty handling is
  // delegated to ListRenderer (and the inline helpers' own empty states),
  // so no screen-level EmptyState here — a list screen with zero records
  // still renders its filter/search affordances plus the list's own empty card.
  if (screen.layout === "list") {
    const inlineTypes = new Set(["filter", "search"]);
    const inlineComponents = screen.components.filter((c) => inlineTypes.has(c.type));
    const mainComponents = screen.components.filter((c) => !inlineTypes.has(c.type));
    return (
      <div className="px-6 py-7 md:px-8 md:py-8">
        <ScreenHeader
          screen={screen}
          recordCount={records.length}
          workspaceViews={workspaceViews}
          activeScreenId={activeScreenId}
          onSelectWorkspaceView={onSelectWorkspaceView}
        />
        <ScreenActionStrip
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
          brandColor={schema.metadata?.brand_color}
        />
        <ScreenOverviewPanel
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
        />
        <div className="flex flex-col gap-4">
          {inlineComponents.length > 0 && (
            <div className="flex flex-wrap items-start gap-4">
              {inlineComponents.map((c) => (
                <ComponentRenderer
                  key={c.component_id}
                  component={c}
                  schema={schema}
                  screen={screen}
                  workspaceId={effectiveWorkspaceId}
                  records={recordsForComponent(c.type)}
                  onNavigateScreen={onNavigateScreen}
                  {...controlProps}
                />
              ))}
            </div>
          )}
          {mainComponents.length > 0 && (
            <div className="flex flex-col gap-6">
              {mainComponents.map((c) => (
                <ComponentRenderer
                  key={c.component_id}
                  component={c}
                  schema={schema}
                  screen={screen}
                  workspaceId={effectiveWorkspaceId}
                  records={visibleRecords}
                  onNavigateScreen={onNavigateScreen}
                  {...controlProps}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Settings layout — Phase 5-A ───
  // All components stack vertically, full-width. No KPI grid, no screen-
  // level empty state — settings components (brand_panel, integration_list)
  // self-handle their own loading/empty/error chrome.
  if (screen.layout === "settings") {
    return (
      <div className="px-6 py-7 md:px-8 md:py-8">
        <ScreenHeader
          screen={screen}
          workspaceViews={workspaceViews}
          activeScreenId={activeScreenId}
          onSelectWorkspaceView={onSelectWorkspaceView}
        />
        <ScreenOverviewPanel
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
        />
        <div className="flex flex-col gap-6">
          {screen.components.map((c) => (
            <ComponentRenderer
              key={c.component_id}
              component={c}
              schema={schema}
              screen={screen}
              workspaceId={effectiveWorkspaceId}
              records={visibleRecords}
              onNavigateScreen={onNavigateScreen}
              {...controlProps}
            />
          ))}
        </div>
      </div>
    );
  }

  // ─── Phase 5-D — industry-shape-aware grid layouts ───
  // All four share the same wrapper shape: page header + optional
  // stat_row above + the main grid below. Differences are only in
  // which grid component renders (the case-routing happens inside
  // ComponentRenderer; this branch just lays them out). Components
  // self-handle empty state.
  const isPhase5DLayout =
    screen.layout === "portfolio_grid" ||
    screen.layout === "gallery_grid" ||
    screen.layout === "schedule_grid" ||
    screen.layout === "production_board";
  if (isPhase5DLayout) {
    const aboveTypes = new Set(["stat_row", "filter", "search"]);
    const aboveComponents = screen.components.filter((c) => aboveTypes.has(c.type));
    const mainComponents = screen.components.filter((c) => !aboveTypes.has(c.type));
    return (
      <div className="px-6 py-7 md:px-8 md:py-8">
        <ScreenHeader
          screen={screen}
          recordCount={records.length}
          workspaceViews={workspaceViews}
          activeScreenId={activeScreenId}
          onSelectWorkspaceView={onSelectWorkspaceView}
        />
        <ScreenActionStrip
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
          brandColor={schema.metadata?.brand_color}
        />
        <ScreenOverviewPanel
          screen={screen}
          recordCount={records.length}
          onAskOperator={onAskOperator}
        />
        <div className="flex flex-col gap-6">
          {aboveComponents.length > 0 && (
            <div className="flex flex-col gap-4">
              {aboveComponents.map((c) => (
                <ComponentRenderer
                  key={c.component_id}
                  component={c}
                  schema={schema}
                  screen={screen}
                  workspaceId={effectiveWorkspaceId}
                  records={recordsForComponent(c.type)}
                  onNavigateScreen={onNavigateScreen}
                  {...controlProps}
                />
              ))}
            </div>
          )}
          {mainComponents.map((c) => (
            <ComponentRenderer
              key={c.component_id}
              component={c}
              schema={schema}
              screen={screen}
              workspaceId={effectiveWorkspaceId}
              records={visibleRecords}
              onNavigateScreen={onNavigateScreen}
              {...controlProps}
            />
          ))}
        </div>
      </div>
    );
  }

  // ─── Default / dashboard layout ───
  // Catches layout === "dashboard" AND any unrecognized layout value.
  // Renders kpi_card components in a 2/4-col grid at the top, then any
  // non-KPI components in a 1/2/3-col grid below. WhatThisOSIsSolving
  // renders here (above the KPIs) only on the actual dashboard screen.
  const kpiComponents = screen.components.filter((c) => c.type === "kpi_card");
  const otherComponents = screen.components.filter((c) => c.type !== "kpi_card");

  return (
    <div className="px-6 py-7 md:px-8 md:py-8">
      <ScreenHeader
        screen={screen}
        recordCount={records.length}
        workspaceViews={workspaceViews}
        activeScreenId={activeScreenId}
        onSelectWorkspaceView={onSelectWorkspaceView}
      />
      <ScreenActionStrip
        screen={screen}
        recordCount={records.length}
        onAskOperator={onAskOperator}
        brandColor={schema.metadata?.brand_color}
      />
      <ScreenOverviewPanel
        screen={screen}
        recordCount={records.length}
        onAskOperator={onAskOperator}
      />
      <div className="flex flex-col gap-6">
        {(screen.screen_id === "dashboard" || screen.layout === "dashboard") && (
          <>
            <DashboardContextStrip
              multiEntity={multiEntity}
              entityCount={entityCount}
              country={country}
              currencies={currencies}
              revenue={revenue}
            />
            <WhatThisOSIsSolving mission={mission} painPoints={painPoints} />
            {/* B3 / 3A — first-run recommendations surfaced as a "First steps"
                card. Renders nothing until/unless there are first_run recs. */}
            <FirstStepsCard
              workspaceId={effectiveWorkspaceId}
              brandColor={schema.metadata?.brand_color}
              onAskOperator={onAskOperator}
            />
            <EstimatedLostRevenue revenue={revenue} painCount={painPoints?.length ?? 0} />
          </>
        )}
        {kpiComponents.length > 0 && (
          // KPI grid lives at the screen level so each kpi_card component
          // renders as one card. Position-indexed seed fallback supplies the
          // visual chrome (trend icon, change text, color, sparkline) until
          // real trend data lands from metric_executions history.
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiComponents.map((kc, i) => (
              <ComponentRenderer onItemClick={onItemClick}
                key={kc.component_id || `kpi-${i}`}
                component={kc}
                schema={schema}
                screen={screen}
                workspaceId={effectiveWorkspaceId}
                records={visibleRecords}
                selectedKpiLabel={selectedKpiLabel}
                kpiSeedFallback={seedData.kpis[i]}
                onNavigateScreen={onNavigateScreen}
                {...controlProps}
              />
            ))}
          </div>
        )}
        {empty && otherComponents.length > 0 && (
          <EmptyState screenLabel={screenLabel} />
        )}
        {!empty && otherComponents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherComponents.map((component) => (
              <ComponentRenderer onItemClick={onItemClick}
                key={component.component_id}
                component={component}
                schema={schema}
                screen={screen}
                workspaceId={effectiveWorkspaceId}
                records={visibleRecords}
                onNavigateScreen={onNavigateScreen}
                {...controlProps}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ screenLabel }: { screenLabel: string }) {
  return (
    <div className="col-span-full rounded-2xl border border-dashed border-zinc-200 bg-white p-10 flex flex-col items-center justify-center text-center">
      <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3 text-zinc-400 text-xl">+</div>
      <p className="text-sm font-medium text-zinc-900">No {screenLabel.toLowerCase()} yet</p>
      <p className="text-xs text-zinc-500 mt-1 max-w-xs">
        Ask your Operator about your first {screenLabel.toLowerCase().replace(/s$/, "")} — they'll help you add it.
      </p>
    </div>
  );
}

function MissingScreenState({
  screenId,
  embedded,
  brandColor,
  onAskOperator,
}: {
  screenId?: string | null;
  embedded?: boolean;
  brandColor: string;
  onAskOperator: (prompt: string) => void;
}) {
  const readable = String(screenId ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "this screen";
  const prompt = `The "${readable}" screen is not installed in the Agentic Operating System yet. Check what data, workflow, or Decision is needed to add it, then route me to the safest next step.`;

  return (
    <div className="px-10 py-16 flex flex-col items-center justify-center text-center">
      <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
        <Plug className="w-7 h-7 text-zinc-400" />
      </div>
      <h2 className="text-lg font-medium text-zinc-900 mb-1">Screen not installed yet</h2>
      <p className="text-sm text-zinc-500 max-w-sm">
        This Agentic Operating System does not currently include {readable}. Ask the Operator to identify the source data, required screen, or approval path before anything changes.
      </p>
      {!embedded && (
        <button
          type="button"
          onClick={() => onAskOperator(prompt)}
          className="mt-5 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: interactiveAccent(brandColor) }}
        >
          Ask Operator to set this up
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function SchemaRenderer({ schema, currentScreenId, workspaceId, userFirstName, embedded = false, onAskOperator, user }: SchemaRendererProps) {
  const navItems = useMemo(
    () => [...schema.navigation.items].sort((a, b) => a.order - b.order),
    [schema.navigation.items],
  );
  const workspaceViews = useMemo(
    () =>
      navItems
        .map((item) => {
          const screen = schema.screens.find((s) => s.screen_id === item.screen_id);
          if (!screen || screen.layout === "settings") return null;
          return {
            screen_id: screen.screen_id,
            label: item.label || screenDisplayLabel(screen) || screen.screen_id,
          };
        })
        .filter((view): view is WorkspaceView => Boolean(view)),
    [navItems, schema.screens],
  );
  const [activeScreenId, setActiveScreenId] = useState(
    currentScreenId || (embedded ? workspaceViews.find((view) => view.screen_id === "dashboard")?.screen_id || workspaceViews[0]?.screen_id || "mission_control" : "mission_control")
  );
  const activeScreen = schema.screens.find((s) => s.screen_id === activeScreenId);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [messages, setMessages] = useState<{ role: string; text: string }[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showPaymentWall, setShowPaymentWall] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const previousScreenRef = useRef<string | null>(null);
  const handleAskOperator = useCallback((prompt: string) => {
    if (onAskOperator) {
      onAskOperator(prompt);
      return;
    }
    if (!embedded) {
      setInput(prompt);
      setIsChatOpen(true);
    }
  }, [embedded, onAskOperator]);
  const handleMissionNav = useCallback((assistantId: string, target?: { tab?: string; prompt?: string }) => {
    if (target?.tab === "integrations") {
      setActiveScreenId("integrations");
      return;
    }
    if (target?.tab === "evolution" || assistantId === "architect") {
      setActiveScreenId("evolution_center");
      return;
    }
    setActiveScreenId("operator");
    if (target?.prompt) {
      handleAskOperator(target.prompt);
    }
  }, [handleAskOperator]);
  const missionPulseScreens = useMemo(
    () =>
      navItems
        .map((item) => {
          const screen = schema.screens.find((s) => s.screen_id === item.screen_id);
          if (!screen) return null;
          if (screen.layout === "dashboard" || screen.layout === "settings") return null;
          return { screen_id: screen.screen_id, label: item.label || screenDisplayLabel(screen) || screen.screen_id };
        })
        .filter((screen): screen is { screen_id: string; label: string } => Boolean(screen)),
    [navItems, schema.screens],
  );
  const primaryWorkspaceLabel = workspaceViews.find((view) => view.screen_id !== "dashboard")?.label || workspaceViews[0]?.label || "workspace";

  // P2 CP6 — one-time Welcome takeover. ?welcome=true is set by the claim
  // redirect; read once on mount, cleared by onEnter (replaceState).
  const [showWelcome, setShowWelcome] = useState(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("welcome") === "true",
  );

  // EVOLUTION-3e — one-shot post-evolution landing. ?screen=...&spotlight=...
  // is set by the BuildingScreen's Show me button. Read once on mount;
  // a useEffect below switches the active screen and the Spotlight
  // component handles dismiss + replaceState cleanup.
  const [evolutionHighlight, setEvolutionHighlight] = useState<{ screen: string; field: string } | null>(
    () => {
      if (typeof window === "undefined") return null;
      const sp = new URLSearchParams(window.location.search);
      const screen = sp.get("screen") || "";
      const field = sp.get("spotlight") || "";
      if (!screen && !field) return null;
      return { screen, field };
    },
  );

  // EVOLUTION-3e — switch active screen when the spotlight params point
  // at a different screen than the one we initialized into. Fires once
  // on mount (and again if highlight changes), no-op if already aligned.
  useEffect(() => {
    if (evolutionHighlight?.screen && evolutionHighlight.screen !== activeScreenId) {
      setActiveScreenId(evolutionHighlight.screen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evolutionHighlight]);

  useEffect(() => {
    if (previousScreenRef.current && previousScreenRef.current !== activeScreenId) {
      notifySafeCheckpoint("screen-change");
    }
    previousScreenRef.current = activeScreenId;
  }, [activeScreenId]);

  // Phase 3 + Phase 5-C — single charter fetch hoisted to the parent so
  // the per-screen ScreenContent (mission + pain_points + dashboard
  // context strip + lost-revenue card) and the IntegrationHub
  // (tools_mentioned for the catalog sort) share one call.
  // hasCharter retains its original semantics — null = unknown (loading);
  // false = legacy/thin, allow procedural seed fallback; true = provisioned.
  const charterWorkspaceId = workspaceId || schema.id || "";
  const [hasCharter, setHasCharter] = useState<boolean | null>(null);
  const [charterMission, setCharterMission] = useState<string>("");
  const [charterPainPoints, setCharterPainPoints] = useState<string[]>([]);
  const [charterToolsMentioned, setCharterToolsMentioned] = useState<string[]>([]);
  const [charterMultiEntity, setCharterMultiEntity] = useState<boolean>(false);
  const [charterEntityCount, setCharterEntityCount] = useState<number>(1);
  const [charterCountry, setCharterCountry] = useState<string>("");
  const [charterCurrencies, setCharterCurrencies] = useState<any[]>([]);
  const [charterRevenue, setCharterRevenue] = useState<{ value: number; currency: string } | null>(null);
  useEffect(() => {
    if (!charterWorkspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/charter/check/${encodeURIComponent(charterWorkspaceId)}`);
        if (!res.ok) { if (!cancelled) setHasCharter(false); return; }
        const j = await res.json();
        if (cancelled) return;
        setHasCharter(!!j.exists);
        setCharterMission(typeof j.mission === "string" ? j.mission : "");
        setCharterPainPoints(Array.isArray(j.pain_points) ? j.pain_points : []);
        setCharterToolsMentioned(Array.isArray(j.tools_mentioned) ? j.tools_mentioned : []);
        setCharterMultiEntity(j.multi_entity === true);
        setCharterEntityCount(typeof j.entity_count === "number" && j.entity_count > 0 ? j.entity_count : 1);
        setCharterCountry(typeof j.country === "string" ? j.country : "");
        setCharterCurrencies(Array.isArray(j.currencies) ? j.currencies : []);
        setCharterRevenue(
          j.stated_annual_revenue && typeof j.stated_annual_revenue === "object"
            && typeof j.stated_annual_revenue.value === "number"
            && typeof j.stated_annual_revenue.currency === "string"
            ? j.stated_annual_revenue
            : null,
        );
      } catch {
        if (!cancelled) setHasCharter(false);
      }
    })();
    return () => { cancelled = true; };
  }, [charterWorkspaceId]);

  // Workspace switcher state — Operating Systems (Applications) are fetched lazily the first time the
  // user clicks the brand area, so we pay zero cost for users who never open it.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [ownedOses, setOwnedOses] = useState<OwnedOS[] | null>(null);
  const [ownedOsesLoading, setOwnedOsesLoading] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [switcherOpen]);

  const handleToggleSwitcher = useCallback(async () => {
    const willOpen = !switcherOpen;
    setSwitcherOpen(willOpen);
    if (willOpen && ownedOses === null && !ownedOsesLoading) {
      setOwnedOsesLoading(true);
      try {
        const res = await fetch("/api/oses-by-owner", { credentials: "include" });
        const data = await res.json();
        setOwnedOses(data?.ok && Array.isArray(data.oses) ? data.oses : []);
      } catch (err) {
        console.error("[switcher] fetch failed:", err);
        setOwnedOses([]);
      } finally {
        setOwnedOsesLoading(false);
      }
    }
  }, [switcherOpen, ownedOses, ownedOsesLoading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      localStorage.setItem(`paid_${schema.id}`, "true");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("screen") === "integrations") {
      setActiveScreenId("integrations");
    }
    // P1 — post-OAuth return: route to Integrations when the callback says so,
    // otherwise keep the existing Operator confirmation path. The param is NOT
    // stripped here — the target screen component reads it and then strips it.
    if (params.get("mcp_connected") && params.get("screen") !== "integrations") {
      setActiveScreenId("operator");
    }
    // Stripe checkout return (?billing=success|cancel): route to the Operator
    // chat so the user sees the payment confirmation / wall. Same contract as
    // mcp_connected — NOT stripped here; Operator reads it, then strips it.
    const billing = params.get("billing");
    if (billing === "success" || billing === "cancel") {
      setActiveScreenId("operator");
    }
  }, [schema.id]);

  const systemInstructionRef = useRef<string>("");
  const drawerHistoryRef = useRef<Array<{ role: "user" | "model"; parts: { text: string }[] }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!activeScreen) return;
    // Use workspaceId (the Agentic Operating System UUID), never schema.id — os_events.os_id is a uuid
    // column and schema.id is "schema-kingkay-test-v1" (version identifier).
    if (workspaceId) {
      trackEvent(workspaceId, {
        event_type: "screen_view",
        screen_id: activeScreen.screen_id,
        metadata: { label: activeScreen.label },
      });
    }
    const baseInstruction =
      activeScreen.intelligence_prompt_template ||
      "You are the Operator — an AI strategist embedded in this user's Agentic Operating System. You know this screen and the data on it intimately.";
    const fullInstruction = `${baseInstruction}\nContext: You are interacting with the user inside their deployed Agentic Operating System.\nCurrent Screen: ${activeScreen.label}\nScreen Type: ${activeScreen.layout}\nPlease provide actionable intelligence, specific formatting, and be concise.`;
    systemInstructionRef.current = fullInstruction;
    drawerHistoryRef.current = [];
    setMessages([]);
    // Clear any anchored KPI selection when navigating between screens, so
    // the highlight + Operator-context label don't bleed across.
    setSelectedItem(null);
  }, [activeScreen?.screen_id, schema.id]);

  const handleSendMessage = async () => {
    if (!input.trim() || !systemInstructionRef.current || isTyping) return;
    const userMsg = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setIsTyping(true);

    // Append user turn to drawer history
    drawerHistoryRef.current.push({ role: "user", parts: [{ text: userMsg }] });

    // Empty model bubble for streaming
    setMessages((prev) => [...prev, { role: "model", text: "" }]);

    try {
      const res = await fetch("/api/harness/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: workspaceId || schema.id,
          agent: "operator",
          screen_id: activeScreenId,
          selected_item: selectedItem,
          history: drawerHistoryRef.current,
        }),
      });

      if (!res.body) throw new Error("No response body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulated += decoder.decode(value, { stream: true });
        setMessages((prev) => {
          const newMsgs = [...prev];
          newMsgs[newMsgs.length - 1] = { role: "model", text: accumulated };
          return newMsgs;
        });
      }

      drawerHistoryRef.current.push({ role: "model", parts: [{ text: accumulated }] });
    } catch (err) {
      console.error("[drawer] chat failed:", err);
      setMessages((prev) => {
        const newMsgs = [...prev];
        newMsgs[newMsgs.length - 1] = { role: "model", text: "Lost signal for a sec. Say that again?" };
        return newMsgs;
      });
    } finally {
      setIsTyping(false);
    }
  };

  // P2 CP6 — one-time Welcome takeover. Renders alone (before normal screen
  // routing); "Take me in" lands the user on the Operator screen, where P1's
  // first-run walkthrough then takes over.
  if (showWelcome) {
    return (
      <WelcomeScreen
        schema={schema}
        onEnter={() => {
          setShowWelcome(false);
          setActiveScreenId("operator");
          window.history.replaceState({}, "", window.location.pathname);
        }}
      />
    );
  }

  const brandColor = schema.metadata.brand_color || DEFAULT_BRAND_COLOR;
  const SYSTEM_SCREEN_IDS = ["mission_control", "operator", "integrations", "evolution_center", "settings", "audit"];
  const isSystemScreen = SYSTEM_SCREEN_IDS.includes(activeScreenId);

  if (!activeScreen && !isSystemScreen) {
    return (
      <MissingScreenState
        screenId={activeScreenId}
        brandColor={brandColor}
        onAskOperator={handleAskOperator}
      />
    );
  }

  const operatorStarterSuggestions = [
    "Where do you keep this data right now?",
    "Find my tracker in my files",
    `Fill my ${primaryWorkspaceLabel} screen from live data`,
    "What should I focus on today?",
  ];

  if (embedded) {
    return (
      <div className="h-full overflow-y-auto bg-zinc-50 text-zinc-900">
        {activeScreen ? (
          <ErrorBoundary scope={`the ${screenDisplayLabel(activeScreen)} screen`}>
            <ScreenContent
              screen={{
                ...activeScreen,
                label: screenDisplayLabel(activeScreen),
              } as ScreenTemplate}
              schema={schema}
              workspaceId={workspaceId}
              onItemClick={(item) => { setSelectedItem(item); setIsChatOpen(true); }}
              selectedKpiLabel={selectedItem?.kind === "kpi" ? selectedItem.label : null}
              hasCharter={hasCharter}
              mission={charterMission}
              painPoints={charterPainPoints}
              multiEntity={charterMultiEntity}
              entityCount={charterEntityCount}
              country={charterCountry}
              currencies={charterCurrencies}
              revenue={charterRevenue}
              workspaceViews={workspaceViews}
              activeScreenId={activeScreenId}
              onSelectWorkspaceView={setActiveScreenId}
              onAskOperator={onAskOperator ? handleAskOperator : undefined}
            />
          </ErrorBoundary>
        ) : (
          <MissingScreenState
            screenId={activeScreenId}
            embedded
            brandColor={brandColor}
            onAskOperator={handleAskOperator}
          />
        )}
        <DetailDrawer />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex bg-zinc-50 text-zinc-900 antialiased font-sans">
      {/* SIDEBAR */}
      <aside
        className={`w-60 shrink-0 flex flex-col bg-white border-r border-zinc-200 fixed inset-y-0 left-0 z-50 transition-transform md:static md:translate-x-0 md:z-auto md:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Workspace switcher */}
        <div ref={switcherRef} className="relative border-b border-zinc-200">
          <button
            type="button"
            onClick={handleToggleSwitcher}
            aria-haspopup="menu"
            aria-expanded={switcherOpen}
            className="w-full h-14 flex items-center gap-2.5 px-4 hover:bg-zinc-50 transition-colors"
          >
            <div
              className="w-6 h-6 rounded-md shrink-0"
              style={{ backgroundColor: brandColor }}
            />
            <div className="flex-1 text-[13px] font-semibold text-zinc-900 truncate text-left">
              {schema.metadata.name}
            </div>
            <ChevronDown
              size={14}
              className={`text-zinc-400 transition-transform ${switcherOpen ? "rotate-180" : ""}`}
            />
          </button>

          {switcherOpen && (
            <div
              role="menu"
              className="absolute left-2 right-2 top-[52px] z-30 bg-white border border-zinc-200 rounded-lg shadow-xl py-1 max-h-[60vh] overflow-y-auto"
            >
              {ownedOsesLoading && (
                <div className="px-3 py-2 text-[12px] text-zinc-400">Loading…</div>
              )}
              {!ownedOsesLoading && ownedOses && ownedOses.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-zinc-500">Just this one for now.</div>
              )}
              {!ownedOsesLoading && ownedOses && ownedOses.length > 0 && (
                <>
                  {ownedOses.map((o) => {
                    const isCurrent = o.id === schema.id || o.id === workspaceId;
                    return (
                      <a
                        key={o.id}
                        role="menuitem"
                        href={`https://${o.subdomain_slug}.sloelabs.com/`}
                        className={`flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-zinc-50 ${
                          isCurrent ? "bg-zinc-50/80" : ""
                        }`}
                      >
                        <div
                          className="w-5 h-5 rounded-md shrink-0"
                          style={{ backgroundColor: o.brand_color || DEFAULT_BRAND_COLOR }}
                        />
                        <span className="flex-1 truncate text-zinc-900">{o.os_name || o.subdomain_slug}</span>
                        {isCurrent && <Check size={14} className="text-zinc-500" />}
                      </a>
                    );
                  })}
                </>
              )}
              <div className="border-t border-zinc-100 mt-1 pt-1">
                <a
                  role="menuitem"
                  href="https://tenant.sloelabs.com/"
                  className="flex items-center justify-between px-3 py-2 text-[12px] text-zinc-600 hover:bg-zinc-50"
                >
                  <span>Manage workspaces</span>
                  <ChevronRight size={12} />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <div className="px-2 mb-1 text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
            Workspace
          </div>
          {!embedded && (
            <button
              onClick={() => { setActiveScreenId("mission_control"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 mb-0.5 rounded-md text-[13px] text-left transition-colors ${
                activeScreenId === "mission_control"
                  ? "bg-zinc-100 text-zinc-900 font-medium"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`}
            >
              <Sparkles size={14} className="shrink-0" />
              <span className="truncate">Mission Control</span>
            </button>
          )}
          {navItems
            // The System group below provides the canonical Settings; drop any
            // schema screen with layout "settings" so it doesn't appear twice.
            .filter((navItem) => schema.screens.find((s) => s.screen_id === navItem.screen_id)?.layout !== "settings")
            .map((navItem) => {
            const active = activeScreenId === navItem.screen_id;
            return (
              <button
                key={navItem.screen_id}
                onClick={() => { setActiveScreenId(navItem.screen_id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 mb-0.5 rounded-md text-[13px] text-left transition-colors ${
                  active
                    ? "bg-zinc-100 text-zinc-900 font-medium"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                  backgroundColor: active ? brandColor : "transparent",
                  border: active ? "none" : "1px solid #d4d4d8",
                }} />
                <span className="truncate">{navItem.label}</span>
              </button>
            );
          })}

          <div className="px-2 mt-6 mb-1 text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
            System
          </div>
          {[
            { id: "operator", label: "Operator", icon: Sparkles },
            { id: "integrations", label: "Integrations", icon: Plug },
            { id: "evolution_center", label: "Evolution", icon: GitBranch },
            { id: "audit", label: "Audit", icon: ClipboardList },
          ].map((item) => {
            const active = activeScreenId === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveScreenId(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 mb-0.5 rounded-md text-[13px] text-left transition-colors ${
                  active
                    ? "bg-zinc-100 text-zinc-900 font-medium"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <Icon size={14} className="shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer / user */}
        <div className="border-t border-zinc-200 p-3">
          <button onClick={() => { setActiveScreenId("settings"); setSidebarOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-zinc-600 hover:bg-zinc-50">
            <Settings size={14} />
            <span>Settings</span>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay — tap to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* MAIN */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 shrink-0 flex items-center justify-between px-4 md:px-6 border-b border-zinc-200 bg-white">
          <div className="flex items-center gap-2 text-[13px] text-zinc-500 min-w-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-1.5 -ml-1 rounded-md text-zinc-600 hover:bg-zinc-100"
              aria-label="Open navigation"
            >
              <Menu size={18} />
            </button>
            <span className="truncate hidden sm:inline">{schema.metadata.name}</span>
            <ChevronRight size={14} className="text-zinc-300 hidden sm:inline" />
            <span className="text-zinc-900 font-medium truncate">{activeScreen?.label || (activeScreenId === "mission_control" ? "Mission Control" : activeScreenId === "operator" ? "Operator" : activeScreenId === "integrations" ? "Integrations" : activeScreenId === "evolution_center" ? "Evolution" : activeScreenId === "settings" ? "Settings" : activeScreenId === "audit" ? "Audit" : activeScreenId)}</span>
          </div>
          <button
            onClick={() => setIsChatOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-zinc-700 bg-white border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
          >
            <Sparkles size={13} />Operator</button>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* X2 — per-screen ErrorBoundary so one broken screen doesn't take
              down navigation. Sidebar lives above this; clicking to another
              screen still works after a crash. Scope strings appear in the
              fallback UI's "Something broke in ___" message. */}
          {activeScreenId === "mission_control" ? (
            <ErrorBoundary scope="Mission Control">
              <MissionControl
                os={{ id: workspaceId || schema.id || "", name: schema.metadata.name }}
                userFirstName={userFirstName}
                brandColor={brandColor}
                workspaceScreens={missionPulseScreens}
                schemaStandardVersion={(schema.metadata as any)?.schema_standard_version || null}
                schemaScreenCount={schema.screens?.length || 0}
                schemaBundleType="schema"
                onNavigateAssistant={handleMissionNav}
              />
            </ErrorBoundary>
          ) : activeScreenId === "operator" ? (
            <ErrorBoundary scope="the Operator">
              <Suspense fallback={
                <div className="flex items-center justify-center h-full w-full">
                  <div className="w-8 h-8 rounded-full border-2 border-zinc-200 animate-spin" style={{ borderTopColor: brandColor }} />
                </div>
              }>
                <OperatorWithThreads
                  schema={schema}
                  workspaceId={workspaceId || schema.id}
                  currentScreenId={activeScreenId}
                  selectedItem={selectedItem}
                  userFirstName={userFirstName}
                  suggestions={operatorStarterSuggestions}
                />
              </Suspense>
            </ErrorBoundary>
          ) : activeScreenId === "evolution_center" ? (
            <ErrorBoundary scope="the Evolution Center">
              <EvolutionCenter workspaceId={workspaceId || schema.id || ""} />
            </ErrorBoundary>
          ) : activeScreenId === "settings" ? (
            <ErrorBoundary scope="Settings">
              <SettingsScreen
                os={{ id: workspaceId || schema.id || "", name: schema.metadata.name, slug: "", brand_color: brandColor }}
                user={user ?? null}
                userFirstName={userFirstName}
                onSignOut={async () => {
                  const ok = await signOut(window.location.origin + "/");
                  if (!ok) window.alert("Couldn't sign out — the sign-in service didn't confirm. You're still signed in on this device. Try again.");
                }}
                onOpenIntegrations={() => setActiveScreenId("integrations")}
              />
            </ErrorBoundary>
          ) : activeScreenId === "audit" ? (
            <ErrorBoundary scope="Audit">
              <AuditScreen workspaceId={workspaceId || ""} />
            </ErrorBoundary>
          ) : activeScreenId === "integrations" ? (
            <ErrorBoundary scope="Integrations">
              <IntegrationHub
                workspaceId={workspaceId || schema.id || ""}
                toolsMentioned={charterToolsMentioned}
                onAskOperator={handleAskOperator}
                screens={(schema.screens || []).map((s: any) => ({ screen_id: s.screen_id, label: s.label }))}
              />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary scope={`the ${activeScreen?.label ?? "current"} screen`}>
              <ScreenContent
                screen={activeScreen!}
                schema={schema}
              workspaceId={workspaceId}
              onItemClick={(item) => { setSelectedItem(item); setIsChatOpen(true); }}
              selectedKpiLabel={selectedItem?.kind === "kpi" ? selectedItem.label : null}
              onNavigateScreen={setActiveScreenId}
              hasCharter={hasCharter}
              mission={charterMission}
              painPoints={charterPainPoints}
              multiEntity={charterMultiEntity}
              entityCount={charterEntityCount}
              country={charterCountry}
              currencies={charterCurrencies}
              revenue={charterRevenue}
              workspaceViews={workspaceViews}
              activeScreenId={activeScreenId}
              onSelectWorkspaceView={setActiveScreenId}
              onAskOperator={handleAskOperator}
            />
            </ErrorBoundary>
          )}
        </div>
      </main>

      {/* ARCHITECT DRAWER */}
      {isChatOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-30"
            onClick={() => setIsChatOpen(false)}
          />
          {(() => {
            // Resolve the context the Operator is anchored on. A clicked KPI
            // takes precedence over the screen-level label; falls back to the
            // active screen name when no item is selected.
            const kpiLabel = selectedItem?.kind === "kpi" ? String(selectedItem.label) : null;
            const contextLabel = kpiLabel || activeScreen?.label || activeScreenId;
            return (
          <div className="fixed top-0 right-0 h-full w-full md:w-[420px] bg-white border-l border-zinc-200 shadow-xl z-40 flex flex-col">
            <div className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <h3 className="text-[13px] font-semibold text-zinc-900">Operator</h3>
                <span className="text-[11px] text-zinc-500">{contextLabel}</span>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
              {messages.length === 0 && (
                <div className="text-[13px] text-zinc-500 leading-relaxed">
                  Ask anything about <span className="font-medium text-zinc-900">{contextLabel}</span>.
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`px-3.5 py-2 rounded-lg max-w-[85%] text-[13px] leading-relaxed ${
                      msg.role === "user"
                        ? "bg-zinc-900 text-white"
                        : "bg-zinc-100 text-zinc-900"
                    }`}
                  >
                    {msg.text || (isTyping && i === messages.length - 1 ? "..." : "")}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-zinc-200">
              <div className="relative">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder={`Ask anything about ${contextLabel}...`}
                  className="w-full pl-3 pr-9 py-2 bg-zinc-50 border border-zinc-200 rounded-md text-[13px] focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isTyping}
                  className="absolute right-1.5 top-1.5 p-1 rounded-md bg-zinc-900 text-white disabled:opacity-30"
                >
                  <Send size={12} />
                </button>
              </div>
            </div>
          </div>
            );
          })()}
        </>
      )}

      {showPaymentWall && (
        <PaymentWall
          onClose={() => setShowPaymentWall(false)}
          osName={schema.metadata.name}
          osId={schema.id}
        />
      )}

      {/* Generic row-detail drawer — subscribes to useOsStore; mounted once
          here at the shell so any screen's row click can open it. */}
      <DetailDrawer />

      {/* EVOLUTION-3e — one-shot post-evolution Spotlight. Only mounts when
          we landed via /?screen=...&spotlight=... AND we're on the right
          screen (the screen-switch effect above re-mounts us after the
          activeScreenId update). Spotlight handles its own dismiss + URL
          cleanup; we just clear local state. */}
      {evolutionHighlight?.field && activeScreenId === evolutionHighlight.screen && (
        <Spotlight
          fieldKey={evolutionHighlight.field}
          onDismiss={() => {
            setEvolutionHighlight(null);
            if (typeof window !== "undefined") {
              const url = new URL(window.location.href);
              url.searchParams.delete("screen");
              url.searchParams.delete("spotlight");
              window.history.replaceState({}, "", url.toString());
            }
          }}
        />
      )}

      {/* New-deploy toast (controllerchange → tap to reload; auto-reload only
          when the page is fresh/hidden — see public/register-sw.js). */}
      <UpdateToast theme="light" accent={brandColor} />
      {!embedded && (
        <AgenticOSWalkthrough
          osId={workspaceId || schema.id || schema.metadata.name}
          osName={schema.metadata.name}
          accent={brandColor}
          onOpenMissionControl={() => setActiveScreenId("mission_control")}
          onOpenIntegrations={() => setActiveScreenId("integrations")}
          onOpenOperator={() => setActiveScreenId("operator")}
          onOpenReceipts={() => setActiveScreenId("mission_control")}
        />
      )}
    </div>
  );
}
